import { createContext, useState, useEffect, useContext } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { useAuth0 } from '@auth0/auth0-react'

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import SaveIcon from '@mui/icons-material/Save';

import { getData, saveData } from './api'

const AutoSaveContext = createContext()

export const AutoSaveProvider = ({ children, onload }) => {
  const { id } = useParams()
  const { pathname } = useLocation()
  const url = pathname.split('/').slice(0, 3).join('/')
  const navigate = useNavigate()
  const [state, setState] = useState({
    name: ''
  })
  const [loading, setLoading] = useState(!!id)
  const [unsaved, setUnsaved] = useState(false)
  const [error, setError] = useState(null)
  const [lastSave, setLastSave] = useState(0)
  const [nextSave, setNextSave] = useState(0)
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          setLoading(true)
          const response = await getData(id, getAccessTokenSilently)
          let data = response.data.data || {}
          if (onload) {
            data = onload(data)
          }
          setState(data || {})
          setLoading(false)
          setUnsaved(false)
        }
      } catch (err) {
        setError(err)
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  useEffect(() => {
    const doSave = async () => {
      console.log({ nextSave, lastSave })
      try {
        if (Object.keys(state).length > 0) {
          setLoading(true)
          if (!state.name) {
            state.name = `Untitled ${new Date().toISOString().substring(0, 10)}`
          }
          const response = await saveData(id || 'new', { url, data: state }, getAccessTokenSilently)
          if (!id) {
            navigate(`${window.location.pathname}/${response.data._id}`)
          }
          setLoading(false)
          setUnsaved(false)
        }
      } catch (err) {
        console.log(err)
        setError(err)
        setLoading(false)
      }
    }
    if (lastSave !== nextSave) {
      setNextSave(lastSave)
      doSave()
    }
  }, [nextSave, lastSave, state])

  const save = () => {
    setNextSave(nextSave + 1)
  }

  return (
    <AutoSaveContext.Provider value={{ state, setState, loading, error, unsaved, setUnsaved, save, id }}>
      {children}
    </AutoSaveContext.Provider>
  )
}

export const useAutoSaveState = (key, initialValue) => {
  const { state, setState, setUnsaved } = useContext(AutoSaveContext)

  const setAutoSaveState = (newValue, cb) => {
    setUnsaved(true)
    if (typeof newValue === 'function') {
      setState((prevState) => {
        return {
          ...prevState,
          [key]: newValue(prevState[key]),
        }
      })
    } else {
      setState(prevState => {
        const newState = {
          ...prevState,
          [key]: newValue,
        }
        if (cb) {
          cb(newState)
        }
        return newState
      })
    }
  }
  if (!state.hasOwnProperty(key)) {
    setState((prevState) => ({
      ...prevState,
      [key]: initialValue,
    }))
  }

  return [(state && state.hasOwnProperty(key)) ? state[key] : initialValue, setAutoSaveState]
}

export const useAutoSave = () => {
  const { loading, unsaved, id, save } = useContext(AutoSaveContext)
  return { loading, unsaved, id, save }
}

export const useLoading = () => {
  const { loading } = useContext(AutoSaveContext)
  return loading
}

export const useUnsaved = () => {
  const { unsaved } = useContext(AutoSaveContext)
  return unsaved
}

export const useId = () => {
  const { id } = useContext(AutoSaveContext)
  return id || null
}

export const useSave = () => {
  const { save } = useContext(AutoSaveContext)
  return save
}

export const SaveButton = () => {
  const { unsaved, save, loading } = useContext(AutoSaveContext)
  const { isAuthenticated } = useAuth0()

  if (!isAuthenticated) {
    return null
  }
  return <>
    <Fab color={unsaved ? "primary" : null} sx={{ position: 'absolute', right: 50 }} onClick={() => save()}>
      <SaveIcon />
    </Fab>
    {loading && (
      <CircularProgress
        size={68}
        sx={{
          color: 'blue',
          position: 'absolute',
          top: -6,
          right: 44,
          zIndex: 1,
        }}
      />
    )}
  </>
}

export const NameField = () => {
  const { id, state, setState, setUnsaved } = useContext(AutoSaveContext)
  const setName = name => {
    setUnsaved(true)
    setState((prevState) => ({
      ...prevState,
      name
    }))
  }
  return <>
    {id && <Grid container spacing={2}>
      <Grid item xs={2} />
      <Grid item xs={8}>
        <TextField variant="standard" margin="none" fullWidth autocomplete="off" label="Name" autoFocus value={state.name} onChange={e => setName(e.target.value)} />
      </Grid>
      <Grid item xs={2} />
    </Grid>}
  </>
}
